import { useFormikContext } from 'formik';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NIGHT_EYE_IMG_URL } from '../../../Process';

const ShopPhotoUpload = () => {
  const { t } = useTranslation();
  const { values, touched, errors, setFieldValue } = useFormikContext();

  const [showModal, setShowModal] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const hasImage = !!values.ShopImage || !!values.oldShopImage;

  // ── Camera Functions ───────────────────────────────────────────────
  const openCamera = async () => {
    setCameraError(null);
    setCameraActive(true);

    try {
      // Try preferred back camera first (good for mobile)
      let stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      // Quick check: if stream appears empty (common on desktop with back camera request)
      if (stream && stream.getVideoTracks().length > 0) {
        const track = stream.getVideoTracks()[0];
        const settings = track.getSettings();
        if (!settings.width || settings.width === 0) {
          // Likely failed → fallback to front/user camera (desktop/laptop default)
          stream.getTracks().forEach(t => t.stop());
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: 'user',  // front-facing / built-in webcam
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          });
        }
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera access error:', err.name, err.message);
      setCameraError(
        err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError'
          ? 'Camera permission denied. Please allow camera access in your browser settings.'
          : err.name === 'NotReadableError' || err.name === 'OverconstrainedError'
          ? 'Could not start camera. Another app might be using it, or try allowing front camera.'
          : 'Failed to open camera. Please try selecting from gallery instead.'
      );
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        const file = new File([blob], `shop-photo-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });

        setFieldValue('ShopImage', file);
        closeCamera();
        setShowModal(false);
      },
      'image/jpeg',
      0.82
    );
  };

  const closeCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setCameraError(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => closeCamera();
  }, []);

  // ── File / Gallery Handling ────────────────────────────────────────
  const handleGalleryClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFieldValue('ShopImage', file);
      closeCamera();
      setShowModal(false);
    }
    // Reset input so same file can be selected again if needed
    e.target.value = '';
  };

  const openModal = () => {
    setShowModal(true);
    closeCamera(); // reset camera state
  };

  return (
    <>
      <label className="text-xs font-bold text-[#0d141b] dark:text-slate-300">
        {t('shopImage')} <span className="text-red-700">*</span>
      </label>

      <div className="relative">
        {/* Clickable preview/upload area */}
        <div
          onClick={openModal}
          className={`border-2 border-dashed cursor-pointer ${
            touched.ShopImage && errors.ShopImage
              ? 'border-red-500'
              : 'border-slate-300 dark:border-slate-600'
          } rounded-xl bg-slate-50 dark:bg-slate-800/50 p-6 sm:p-8 flex flex-col items-center justify-center gap-4 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors min-h-[260px]`}
        >
          {hasImage ? (
            <div className="w-full relative group/image">
              <img
                src={
                  values.ShopImage
                    ? URL.createObjectURL(values.ShopImage)
                    : `${NIGHT_EYE_IMG_URL}/SmartCollection/ShopImage/${values.oldShopImage}`
                }
                alt="Shop preview"
                className="w-full h-64 object-cover rounded-lg border border-slate-200 dark:border-slate-700"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setFieldValue('ShopImage', null);
                }}
                className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full opacity-0 group-hover/image:opacity-100 transition-opacity"
              >
                <span className="material-symbols-outlined text-lg">close</span>
              </button>
            </div>
          ) : (
            <>
              <div className="h-16 w-16 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                <span className="material-symbols-outlined text-5xl">add_a_photo</span>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-primary">{t('clickToUpload')}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('dragDrop')} or take photo
                </p>
              </div>
            </>
          )}
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpg,image/jpeg,image/png,image/gif"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Formik validation error */}
      {touched.ShopImage && errors.ShopImage && (
        <p className="text-red-700 text-xs font-bold mt-1.5">{errors.ShopImage}</p>
      )}

      {/* ── Modal ──────────────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Add Shop Photo
              </h3>
              <button
              type="button"
                onClick={() => {
                  setShowModal(false);
                  closeCamera();
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {cameraActive ? (
                <div className="space-y-5">
                  <div className="relative bg-black rounded-xl overflow-hidden aspect-[4/3] border border-gray-700 shadow-inner">
                    <video
                      ref={videoRef}
                      className="w-full h-full object-cover"
                      autoPlay
                      playsInline
                      muted
                    />
                    <canvas ref={canvasRef} className="hidden" />
                  </div>

                  {cameraError && (
                    <p className="text-red-600 text-sm text-center bg-red-50 dark:bg-red-950/30 p-3 rounded">
                      {cameraError}
                    </p>
                  )}

                  <div className="flex gap-4">
                    <button
                    type="button"
                      onClick={closeCamera}
                      className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                    type="button"
                      onClick={capturePhoto}
                      disabled={!!cameraError}
                      className="flex-1 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      Capture Photo
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-5">
                  <button
                  type="button"
                    onClick={openCamera}
                    className="flex flex-col items-center gap-3 py-8 border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span className="material-symbols-outlined text-6xl text-blue-600">photo_camera</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">Take Photo</span>
                  </button>

                  <button
                  type="button"
                    onClick={handleGalleryClick}
                    className="flex flex-col items-center gap-3 py-8 border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span className="material-symbols-outlined text-6xl text-green-600">photo_library</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">Gallery</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShopPhotoUpload;